export interface DnsCheckResult {
  aliDoh: { ipv4: string[]; ipv6: string[] } | null;
  googleDoh: { ipv4: string[]; ipv6: string[] } | null;
  dnsConsistent: boolean;
  domain: string;
}

async function fetchWithTimeout(url: string, timeoutMs: number = 5000): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

async function queryAliDoH(domain: string, type: string = "A"): Promise<string[]> {
  try {
    const res = await fetchWithTimeout(
      `https://dns.alidns.com/resolve?name=${encodeURIComponent(domain)}&type=${type}`,
      5000
    );
    if (!res.ok) return [];
    const data = await res.json();
    return (data.Answer || [])
      .filter((a: any) => a.type === (type === "A" ? 1 : 28))
      .map((a: any) => a.data);
  } catch {
    return [];
  }
}

async function queryGoogleDoH(domain: string, type: string = "A"): Promise<string[]> {
  try {
    const res = await fetchWithTimeout(
      `https://dns.google/resolve?name=${encodeURIComponent(domain)}&type=${type}`,
      5000
    );
    if (!res.ok) return [];
    const data = await res.json();
    return (data.Answer || [])
      .filter((a: any) => a.type === (type === "A" ? 1 : 28))
      .map((a: any) => a.data);
  } catch {
    return [];
  }
}

export async function checkDns(domain: string): Promise<DnsCheckResult> {
  const [aliV4, aliV6, googleV4, googleV6] = await Promise.all([
    queryAliDoH(domain, "A"),
    queryAliDoH(domain, "AAAA"),
    queryGoogleDoH(domain, "A"),
    queryGoogleDoH(domain, "AAAA"),
  ]);

  const aliDoh = { ipv4: aliV4, ipv6: aliV6 };
  const googleDoh = { ipv4: googleV4, ipv6: googleV6 };

  const allV4Sets = [aliV4, googleV4].filter((s) => s.length > 0);

  let dnsConsistent = true;
  if (allV4Sets.length > 1) {
    const first = new Set(allV4Sets[0]);
    dnsConsistent = allV4Sets.every((s) => s.some((ip: string) => first.has(ip)));
  }

  return {
    aliDoh,
    googleDoh,
    dnsConsistent,
    domain,
  };
}
